/**
 * Server-side consent ledger — "One TruWe Consent".
 *
 * Consent here is PERSISTENT: once a customer explicitly says yes to a
 * channel, that grant stands until they explicitly withdraw it. Nothing in
 * this file may downgrade a standing grant unless the caller marks the write
 * as explicit (`explicit: true` — a real customer action such as toggling a
 * channel off, an unsubscribe link, or a STOP reply). Everything else —
 * defaults, dismissed popups, omitted fields, blocked browser permission,
 * retries — is recorded in history but never turns a grant off.
 *
 * `recordMarketingConsent` keeps the original advertising/banner contract.
 * `recordConsentChannels` is the unified writer used by every other capture
 * point: it appends an immutable row to `marketing_consent_events` AND
 * settles the current per-channel state in `consent_preferences`, via the
 * `record_consent` database function so both stay in step.
 *
 * Public (unauthenticated) on purpose — visitors choose before signing in.
 * Nothing here is trusted for authorization: rows are keyed by the caller's
 * anonymous ID and best-effort linked to a known customer/account.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { CONSENT_CHANNELS } from "@/lib/consent/channels";

const InputSchema = z.object({
  anonymousId: z.string().min(8).max(64),
  status: z.enum(["granted", "denied"]),
  source: z.string().min(1).max(40).default("banner"),
  channel: z.string().min(1).max(40).default("advertising"),
  policyVersion: z.string().min(1).max(40),
  pageUrl: z.string().max(500).optional(),
  userAgent: z.string().max(400).optional(),
  explicit: z.boolean().default(true),
});

/**
 * Best-effort identity stitch from an anonymous visitor ID.
 *
 * A missing lead record is NOT a signal about consent — it only means we
 * cannot widen the identity yet, so the saved anonymous state still applies.
 */
async function resolveIdentity(
  supabaseAdmin: typeof import("@/integrations/supabase/client.server")["supabaseAdmin"],
  anonymousId: string,
): Promise<{ customerId: string | null; userId: string | null }> {
  try {
    const { data: lead } = await supabaseAdmin
      .from("lead_profiles")
      .select("customer_id, auth_user_id")
      .eq("anonymous_id", anonymousId)
      .maybeSingle();
    return {
      customerId: (lead?.customer_id as string | null) ?? null,
      userId: (lead?.auth_user_id as string | null) ?? null,
    };
  } catch {
    return { customerId: null, userId: null };
  }
}

export const recordMarketingConsent = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => InputSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeConsent } = await import("@/lib/consent/state.server");
    const { customerId, userId } = await resolveIdentity(supabaseAdmin, data.anonymousId);

    const result = await writeConsent({
      channel: data.channel as never,
      status: data.status,
      source: data.source,
      policyVersion: data.policyVersion,
      identity: { anonymousId: data.anonymousId, customerId, userId },
      explicit: data.explicit,
      pageUrl: data.pageUrl,
      userAgent: data.userAgent,
    });

    return { ok: result.ok, status: result.status };
  });

const ChannelsSchema = z.object({
  anonymousId: z.string().min(8).max(64),
  source: z.string().min(1).max(40),
  policyVersion: z.string().min(1).max(40),
  pageUrl: z.string().max(500).optional(),
  userAgent: z.string().max(400).optional(),
  /** False for background/derived writes that must never withdraw consent. */
  explicit: z.boolean().default(true),
  channels: z
    .array(
      z.object({
        channel: z.enum(CONSENT_CHANNELS),
        status: z.enum(["granted", "denied"]),
      }),
    )
    .min(1)
    .max(8),
});

export const recordConsentChannels = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => ChannelsSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeConsent } = await import("@/lib/consent/state.server");
    const { customerId, userId } = await resolveIdentity(supabaseAdmin, data.anonymousId);
    const identity = { anonymousId: data.anonymousId, customerId, userId };

    let failed = 0;
    const settled: Record<string, string> = {};
    for (const entry of data.channels) {
      const result = await writeConsent({
        channel: entry.channel,
        status: entry.status,
        source: data.source,
        policyVersion: data.policyVersion,
        identity,
        explicit: data.explicit,
        pageUrl: data.pageUrl,
        userAgent: data.userAgent,
      });
      if (!result.ok) failed += 1;
      else settled[entry.channel] = result.status;
    }

    // Keep the legacy lead flag in sync with the SETTLED state, never with
    // the requested one — otherwise a non-explicit "off" would undo a
    // standing grant through the back door.
    const whatsapp = settled["whatsapp"];
    if (whatsapp === "granted" || whatsapp === "withdrawn" || whatsapp === "denied") {
      try {
        await supabaseAdmin
          .from("lead_profiles")
          .update({ consent_whatsapp: whatsapp === "granted" })
          .eq("anonymous_id", data.anonymousId);
      } catch {
        /* best effort — the authoritative state is consent_preferences */
      }
    }

    // Only a deliberate withdrawal tears down live push delivery. An
    // inactive or blocked subscription is a delivery matter, not consent.
    if (settled["push"] === "withdrawn") {
      try {
        await supabaseAdmin
          .from("push_subscriptions")
          .update({ revoked_at: new Date().toISOString() } as never)
          .eq("anonymous_id", data.anonymousId)
          .is("revoked_at" as never, null);
      } catch {
        /* best effort */
      }
    }

    return { ok: failed === 0, settled };
  });

/** Current per-channel state for a visitor (used by the preference centre). */
export const getConsentPreferences = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => z.object({ anonymousId: z.string().min(8).max(64) }).parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { customerId, userId } = await resolveIdentity(supabaseAdmin, data.anonymousId);

    // Read across EVERY identity this person is known by, so a saved choice
    // is restored after login, logout or on a brand new session.
    const identityFilters: Array<[string, string]> = [];
    if (userId) identityFilters.push(["user_id", userId]);
    if (customerId) identityFilters.push(["customer_id", customerId]);
    identityFilters.push(["anonymous_id", data.anonymousId]);

    const rank = (status: string) => (status === "granted" ? 2 : status === "withdrawn" ? 1 : 0);
    const preferences: Record<string, string> = {};
    let readOk = false;

    for (const [column, value] of identityFilters) {
      // Retry once: a database error must leave a saved grant untouched and
      // unreported rather than read as a refusal.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const { data: rows, error } = await supabaseAdmin
          .from("consent_preferences")
          .select("channel, status, updated_at")
          .eq(column as never, value)
          .order("updated_at", { ascending: false });
        if (error) {
          if (attempt === 1) console.warn("[consent-log] preference read failed", error.message);
          continue;
        }
        readOk = true;
        for (const row of rows ?? []) {
          const current = preferences[row.channel];
          // Strongest standing decision wins across identities; a grant is
          // never lost because a weaker anonymous row also exists.
          if (!current || rank(row.status) > rank(current)) preferences[row.channel] = row.status;
        }
        break;
      }
    }

    if (!readOk) {
      return {
        ok: false as const,
        preferences: {} as Record<string, string>,
        pushSubscriptionActive: false,
      };
    }

    // Honest push state: granted consent means nothing without a live
    // subscription row for this device. Delivery only — never consent.
    let pushSubscriptionActive = false;
    try {
      const { count } = await supabaseAdmin
        .from("push_subscriptions" as never)
        .select("id", { count: "exact", head: true })
        .eq("anonymous_id" as never, data.anonymousId)
        .is("revoked_at" as never, null);
      pushSubscriptionActive = (count ?? 0) > 0;
    } catch {
      /* best effort */
    }

    return { ok: true as const, preferences, pushSubscriptionActive };
  });
